import type { CaseUpdateHandlers } from 'features/mdCases/caseProposalAPI'
import React, { createContext, useContext } from 'react'

/**
 * Context that exposes case-update handlers (the same mutators
 * `useMdCaseDetails()` returns) to chat-side components like
 * `ProposalCard`. Defined here in `/features/mdCases` rather than
 * the page provider so chat components — which live in
 * `/features/chatbot` — can read it without breaking the
 * `/features` → `/pages` import boundary.
 *
 * The case page (`MdCaseDetailsPage` / `CaseDetails`) wraps its
 * subtree in `<CaseUpdateHandlersProvider handlers={...}>`;
 * `ProposalCard` reads via `useCaseUpdateHandlers()`. Returns null
 * outside the provider — graceful degrade for chats not on the
 * case page.
 */
const CaseUpdateHandlersContext = createContext<CaseUpdateHandlers | null>(null)

export const CaseUpdateHandlersProvider: React.FC<{
  handlers: CaseUpdateHandlers
  children: React.ReactNode
}> = ({ handlers, children }) => (
  <CaseUpdateHandlersContext.Provider value={handlers}>
    {children}
  </CaseUpdateHandlersContext.Provider>
)

export function useCaseUpdateHandlers(): CaseUpdateHandlers | null {
  return useContext(CaseUpdateHandlersContext)
}
